/** @jsxImportSource theme-ui */
import { useState } from 'react';
import { Flex, Box, Container } from 'theme-ui';
import { FormikProps } from 'formik';
import { GrayFormikField } from '@/components/GrayFormikField';
import Button from '@/components/Button';
import Error from '@/components/Error';
import {
    useGetCompaniesQuery,
    useGetTicketAreasQuery,
    useGetTicketStatusesQuery,
    useGetTicketTypesQuery,
} from '@/store/apiService';
import { toSelectOptions } from './common';
import { CreateTicket, UpdateTicket } from '@/types/types';

export type TicketFormValues = CreateTicket & Partial<Pick<UpdateTicket, 'Id' | 'CaseStatusId'>>;

interface Props extends FormikProps<TicketFormValues> {
    mode: 'create' | 'edit';
    additionalErrors?: React.ReactNode;
    addToFooter?: React.ReactNode;
}

const TicketForm: React.FC<Props> = ({ mode, additionalErrors, addToFooter, ...formProps }) => {
    const statuses = useGetTicketStatusesQuery();
    const types = useGetTicketTypesQuery();
    const areas = useGetTicketAreasQuery();

    const [companySearch, setCompanySearch] = useState('');
    const companies = useGetCompaniesQuery(
        { Take: 20, Search: companySearch },
        { skip: mode !== 'create' }
    );

    const statusOptions = toSelectOptions(statuses.data?.Results);
    const typeOptions = toSelectOptions(types.data?.Results);
    const areaOptions = toSelectOptions(areas.data?.Results);

    const companyItems = (companies.data?.Results ?? []).map((company) => ({
        value: company.Id as string,
        label: company.Name ?? (company.Id as string),
    }));

    return (
        <Flex backgroundColor="background" sx={{ flexDirection: 'column', borderRadius: 'sm' }}>
            <Flex
                p="2"
                sx={{
                    gap: 2,
                    '@media only screen and (max-width:60em)': { flexDirection: 'column' },
                    borderRadius: 'sm',
                }}
            >
                <Box
                    p="3"
                    sx={{
                        flexBasis: '60%',
                        borderRadius: 'sm',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: 'gray.2',
                    }}
                >
                    {mode === 'create' ? (
                        <GrayFormikField
                            name="CompanyId"
                            value={formProps.values.CompanyId}
                            type="dropdownsearch"
                            label="Company"
                            placeholder="Search companies"
                            onSearchChange={(val: string) => setCompanySearch(val)}
                            onSelect={(item: { value: string } | null) =>
                                formProps.setFieldValue('CompanyId', item?.value ?? '')
                            }
                            items={companyItems}
                            isLoading={companies.isFetching}
                            total={companies.data?.Total}
                            required
                        />
                    ) : null}
                    <GrayFormikField name="Title" type="text" label="Title" required />
                    <GrayFormikField
                        name="Description"
                        type="textarea"
                        label="Description"
                        required
                    />
                </Box>

                <Flex
                    sx={{
                        gap: 2,
                        flexBasis: '40%',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        p="3"
                        sx={{
                            borderRadius: 'sm',
                            borderStyle: 'solid',
                            borderWidth: '1px',
                            borderColor: 'gray.2',
                        }}
                    >
                        <GrayFormikField
                            label="Type"
                            type="select"
                            name="CaseTypeId"
                            options={typeOptions}
                            isLoading={types.isFetching}
                            required
                        />
                        <GrayFormikField
                            label="Area"
                            type="select"
                            name="CaseAreaId"
                            options={areaOptions}
                            isLoading={areas.isFetching}
                            required
                        />
                        {mode === 'edit' ? (
                            <GrayFormikField
                                label="Status"
                                type="select"
                                name="CaseStatusId"
                                options={statusOptions}
                                isLoading={statuses.isFetching}
                            />
                        ) : null}
                    </Box>
                </Flex>
            </Flex>
            {additionalErrors ? (
                <Box p="2">
                    <Container variant="error-message">
                        <Error error={additionalErrors as any} />
                    </Container>
                </Box>
            ) : null}
            <Flex p="2" sx={{ width: '100%', gap: 2, justifyContent: 'flex-end' }}>
                {addToFooter}
                <Button
                    isLoading={formProps.isSubmitting}
                    disabled={formProps.isSubmitting}
                    type="submit"
                    onClick={formProps.submitForm}
                >
                    Save
                </Button>
            </Flex>
        </Flex>
    );
};

export default TicketForm;
